import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import { Song } from '../utils/redux/playlistSlice'
import api from '../utils/api'

export interface AudioPlayerContextType {
  currentSong: Song | null
  queue: Song[]
  currentIndex: number
  isPlaying: boolean
  volume: number
  duration: number
  currentTime: number
  isLoading: boolean
  playSong: (song: Song, songQueue?: Song[]) => void
  playQueue: (songs: Song[], startIndex?: number) => void
  togglePlayPause: () => void
  playNext: () => void
  playPrevious: () => void
  setVolume: (volume: number) => void
  seek: (time: number) => void
  clearQueue: () => void
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined)

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext)
  if (!context) {
    throw new Error('useAudioPlayer must be used within AudioPlayerProvider')
  }
  return context
}

interface AudioPlayerProviderProps {
  children: React.ReactNode
}

export const AudioPlayerProvider: React.FC<AudioPlayerProviderProps> = ({ children }) => {
  const [currentSong, setCurrentSong] = useState<Song | null>(null)
  const [queue, setQueue] = useState<Song[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolumeState] = useState(1)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [audioUrls, setAudioUrls] = useState<Record<string, string>>({})

  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Initialize audio element once
  useEffect(() => {
    const audio = new Audio()
    audioRef.current = audio

    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime)
    const handleDurationChange = () => setDuration(audio.duration)
    const handleVolumeChange = () => setVolumeState(audio.volume)
    
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('durationchange', handleDurationChange)
    audio.addEventListener('volumechange', handleVolumeChange)

    return () => {
      audio.pause()
      audio.src = ''
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('durationchange', handleDurationChange)
      audio.removeEventListener('volumechange', handleVolumeChange)
      Object.values(audioUrls).forEach(url => URL.revokeObjectURL(url))
    }
  }, [audioUrls])

  const fetchAudioBlob = useCallback(async (song: Song): Promise<string> => {
    // Check if we already have the blob URL
    if (audioUrls[song._id]) {
      return audioUrls[song._id]
    }

    setIsLoading(true)
    try {
      const response = await api.get(`/song/download/${song.file}`, {
        responseType: 'blob',
      })

      if (!response.data || response.data.size === 0) {
        throw new Error('Received empty audio file')
      }

      const blobUrl = URL.createObjectURL(response.data)
      setAudioUrls(prev => ({ ...prev, [song._id]: blobUrl }))
      return blobUrl
    } catch (error) {
      console.error('Error fetching audio:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [audioUrls])

  const loadAndPlaySong = useCallback(async (song: Song) => {
    const audio = audioRef.current
    if (!audio) return

    try {
      // Always pause first
      audio.pause()
      
      const blobUrl = await fetchAudioBlob(song)
      
      // Set new source and load
      audio.src = blobUrl
      audio.load()
      
      // Wait a bit for load to complete
      await new Promise(resolve => setTimeout(resolve, 100))
      
      await audio.play()
      setCurrentSong(song)
    } catch (error) {
      console.error('Error loading song:', error)
      setIsLoading(false)
    }
  }, [fetchAudioBlob])

  const playSong = useCallback((song: Song, songQueue?: Song[]) => {
    if (songQueue) {
      setQueue(songQueue)
      const index = songQueue.findIndex(s => s._id === song._id)
      setCurrentIndex(index >= 0 ? index : 0)
    } else {
      setQueue([song])
      setCurrentIndex(0)
    }
    loadAndPlaySong(song)
  }, [loadAndPlaySong])

  const playQueue = useCallback((songs: Song[], startIndex: number = 0) => {
    if (songs.length === 0) return
    
    setQueue(songs)
    setCurrentIndex(startIndex)
    loadAndPlaySong(songs[startIndex])
  }, [loadAndPlaySong])

  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return

    if (audio.paused) {
      audio.play().catch(console.error)
    } else {
      audio.pause()
    }
  }, [])

  const playNext = useCallback(() => {
    if (queue.length === 0) return
    
    const nextIndex = currentIndex + 1
    if (nextIndex < queue.length) {
      setCurrentIndex(nextIndex)
      loadAndPlaySong(queue[nextIndex])
    } else {
      // Loop back to start
      setCurrentIndex(0)
      loadAndPlaySong(queue[0])
    }
  }, [queue, currentIndex, loadAndPlaySong])

  const playPrevious = useCallback(() => {
    if (queue.length === 0) return
    
    const audio = audioRef.current
    if (audio && audio.currentTime > 3) {
      // If more than 3 seconds into the song, restart it
      audio.currentTime = 0
    } else {
      // Go to previous song
      const prevIndex = currentIndex - 1
      if (prevIndex >= 0) {
        setCurrentIndex(prevIndex)
        loadAndPlaySong(queue[prevIndex])
      } else {
        // Loop to end
        setCurrentIndex(queue.length - 1)
        loadAndPlaySong(queue[queue.length - 1])
      }
    }
  }, [queue, currentIndex, loadAndPlaySong])

  const setVolume = useCallback((newVolume: number) => {
    const audio = audioRef.current
    if (!audio) return
    
    audio.volume = Math.max(0, Math.min(1, newVolume))
  }, [])

  const seek = useCallback((time: number) => {
    const audio = audioRef.current
    if (!audio) return
    
    audio.currentTime = time
  }, [])

  const clearQueue = useCallback(() => {
    const audio = audioRef.current
    if (audio) {
      audio.pause()
      audio.src = ''
    }
    setQueue([])
    setCurrentSong(null)
    setCurrentIndex(0)
    setIsPlaying(false)
  }, [])

  // Handle song ended event
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleEnded = () => {
      if (queue.length > 0 && currentIndex < queue.length - 1) {
        // Play next song
        const nextIndex = currentIndex + 1
        setCurrentIndex(nextIndex)
        loadAndPlaySong(queue[nextIndex])
      } else {
        // End of queue
        setIsPlaying(false)
      }
    }

    audio.addEventListener('ended', handleEnded)
    return () => {
      audio.removeEventListener('ended', handleEnded)
    }
  }, [queue, currentIndex, loadAndPlaySong])

  const value: AudioPlayerContextType = {
    currentSong,
    queue,
    currentIndex,
    isPlaying,
    volume,
    duration,
    currentTime,
    isLoading,
    playSong,
    playQueue,
    togglePlayPause,
    playNext,
    playPrevious,
    setVolume,
    seek,
    clearQueue,
  }

  return <AudioPlayerContext.Provider value={value}>{children}</AudioPlayerContext.Provider>
}
